import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // ✅ Imported useNavigate for the Back button
import { Bell, UserPlus, Heart, MessageCircle, X, Check, Archive, ChevronDown, ChevronUp, Loader2, Clock, AlertCircle, ArrowLeft } from "lucide-react"; // ✅ Added ArrowLeft
import httpClient from "../Api/axios";
import webSocketService from "../Api/webSocketService";

const Notification = ({ userId }) => {
    const navigate = useNavigate(); // ✅ Initialize navigate hook
    
    // Get user ID from prop or localStorage
    const currentUserId = userId || localStorage.getItem("userId");
    
    // UI State
    const [activeTab, setActiveTab] = useState("general");
    const [showIgnored, setShowIgnored] = useState(false);
    
    // Data State
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // ✅ WebSocket real-time listener on component mount
    useEffect(() => {
        if (!currentUserId) {
            setError("User ID not found. Please log in again.");
            setLoading(false);
            return;
        }

        console.log("🔗 Connecting to WebSocket for notifications - User:", currentUserId);
        
        // Connect to WebSocket for real-time notifications
        webSocketService.connect(currentUserId, (newNotification) => {
            console.log("🔴 Real-time notification arrived:", newNotification);
            
            // Add new notification to the top of the list
            setNotifications((prev) => [newNotification, ...prev]);
        });

        return () => {
            console.log("❌ Disconnecting WebSocket");
            webSocketService.disconnect();
        };
    }, [currentUserId]);

    // 1️⃣ Fetch notifications on component mount + periodic polling fallback
    useEffect(() => {
        if (!currentUserId) return;

        const fetchNotifications = async (isBackground = false) => {
            try {
                // Fetch all notifications
                const res = await httpClient.get(`/api/v1/notifications/${currentUserId}`);
                setNotifications(res.data || []);
                
                // Fetch unread count
                const unreadRes = await httpClient.get(`/api/v1/notifications/${currentUserId}/unread-count`);
                setUnreadCount(unreadRes.data?.unreadCount || 0);
                
                setError(null);
            } catch (err) {
                console.error("❌ Failed to load notifications:", err);
                if (!isBackground) {
                    setError("Failed to load notifications. Please try again.");
                }
            } finally {
                if (!isBackground) setLoading(false);
            }
        };

        // Initial fetch
        fetchNotifications(false);
        
        // 🔴 Polling fallback (15 seconds) - WebSocket handles real-time
        const intervalId = setInterval(() => fetchNotifications(true), 15000);
        
        return () => clearInterval(intervalId);
    }, [currentUserId]);

    // ✅ Go Back handler
    const goBack = () => navigate(-1);

    // 2️⃣ Handle Accept Connection Request
    const handleAccept = async (notif) => {
        // Optimistic UI update
        setNotifications((prev) => prev.filter((n) => n.notificationId !== notif.notificationId));

        try {
            // Accept the connection (using referenceId as connectionId)
            if (notif.referenceId) {
                await httpClient.put(`/api/v1/connections/accept/${notif.referenceId}`);
                console.log("✅ Connection accepted:", notif.referenceId);
            }

            // Delete the notification
            await httpClient.delete(`/api/v1/notifications/${notif.notificationId}`);
            console.log("✅ Notification deleted:", notif.notificationId);
            
        } catch (err) {
            console.error("❌ Error accepting request:", err);
            
            // If 404/400, still delete the notification (connection already processed)
            if (err.response && (err.response.status === 404 || err.response.status === 400)) {
                try {
                    await httpClient.delete(`/api/v1/notifications/${notif.notificationId}`);
                    console.log("✅ Notification cleaned up after error");
                } catch (deleteErr) {
                    console.error("❌ Failed to delete notification:", deleteErr);
                }
            } else {
                // Restore notification on error
                setNotifications((prev) => [notif, ...prev]);
                alert("❌ Failed to accept request. Please try again.");
            }
        }
    };

    // 3️⃣ Handle Ignore Connection Request
    const handleIgnore = async (notif) => {
        // Optimistic UI update
        setNotifications(prev => prev.map(n => 
            n.notificationId === notif.notificationId ? {...n, status: "IGNORED"} : n
        ));
        
        try {
            await httpClient.put(`/api/v1/notifications/${notif.notificationId}/ignore`);
            console.log("✅ Notification ignored:", notif.notificationId);
        } catch (err) { 
            console.error("❌ Error ignoring notification:", err);
            // Restore on error
            setNotifications(prev => prev.map(n => 
                n.notificationId === notif.notificationId ? {...n, status: "PENDING"} : n
            ));
        }
    };

    // 4️⃣ Handle Permanent Delete
    const handleDeletePermanent = async (id) => {
        // Optimistic UI update
        setNotifications((prev) => prev.filter((n) => n.notificationId !== id));
        
        try {
            await httpClient.delete(`/api/v1/notifications/${id}`);
            console.log("✅ Notification permanently deleted:", id);
        } catch (err) { 
            console.error("❌ Error deleting notification:", err);
            // Refetch on error
            window.location.reload();
        }
    };

    // 5️⃣ Filtering Logic
    const generalNotifications = notifications.filter(n => n.type !== "CONNECTION_REQUEST");
    
    const activeRequests = notifications.filter(n => 
        n.type === "CONNECTION_REQUEST" && (!n.status || n.status === "PENDING")
    );
    
    const ignoredRequests = notifications.filter(n => 
        n.type === "CONNECTION_REQUEST" && n.status === "IGNORED"
    );

    // 6️⃣ Helper function for notification icons
    const getIcon = (type) => {
        switch (type) {
            case "LIKE": 
                return <Heart size={18} color="#ff4d4d" fill="#ff4d4d" />;
            case "COMMENT": 
                return <MessageCircle size={18} color="#70b5f9" />;
            case "CONNECTION_ACCEPTED":
                return <Check size={18} color="#00ba7c" />;
            case "CONNECTION_REJECTED":
                return <AlertCircle size={18} color="#ff4d4d" />;
            case "CONNECTION_REQUEST":
                return <UserPlus size={18} color="#1d9bf0" />;
            default: 
                return <Bell size={18} color="#fff" />;
        }
    };

    // Loading state
    if (loading) return (
        <div style={styles.loadingContainer}>
            <Loader2 size={40} style={{animation: 'spin 1s linear infinite'}} />
            <p>Loading notifications...</p>
        </div>
    );

    return (
        <div className="notif-page">
            <div className="notif-container">
                
                {/* ✅ PAGE HEADER WITH BACK BUTTON */}
                <div className="page-header">
                    <button onClick={goBack} className="back-btn">
                        <ArrowLeft size={24} color="#fff" />
                    </button>
                    {/* ✅ Using unreadCount to fix the ESLint warning */}
                    <h2>Notifications {unreadCount > 0 && `(${unreadCount})`}</h2>
                </div>

                {/* TABS HEADER */}
                <div className="tabs-header">
                    <button 
                        className={`tab-btn ${activeTab === "general" ? "active" : ""}`} 
                        onClick={() => setActiveTab("general")}
                    >
                        Notifications
                        {generalNotifications.length > 0 && (
                            <span className="badge">{generalNotifications.length}</span>
                        )}
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === "requests" ? "active" : ""}`} 
                        onClick={() => setActiveTab("requests")}
                    >
                        Requests
                        {activeRequests.length > 0 && (
                            <span className="badge red">{activeRequests.length}</span>
                        )}
                    </button>
                </div>

                {/* ERROR MESSAGE */}
                {error && (
                    <div className="error-banner">
                        <AlertCircle size={18} />
                        <span>{error}</span>
                    </div>
                )}

                {/* CONTENT AREA */}
                <div className="notif-content">
                    
                    {/* TAB 1: GENERAL NOTIFICATIONS */}
                    {activeTab === "general" && (
                        <div className="list-group">
                            {generalNotifications.length === 0 ? (
                                <div className="empty-state">
                                    <Bell size={40} />
                                    <p>No new activity</p>
                                </div>
                            ) : (
                                generalNotifications.map(notif => (
                                    <div key={notif.notificationId} className="notif-item">
                                        <div className="icon-box">
                                            {getIcon(notif.type)}
                                        </div>
                                        <div className="content">
                                            <p className="message">
                                                <span className="bold-name">{notif.senderName}</span> {notif.message}
                                            </p>
                                            <span className="time">
                                                {new Date(notif.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <button 
                                            className="delete-btn" 
                                            onClick={() => handleDeletePermanent(notif.notificationId)}
                                            title="Delete notification"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {/* TAB 2: CONNECTION REQUESTS */}
                    {activeTab === "requests" && (
                        <div className="list-group">
                            
                            {/* Active Requests */}
                            {activeRequests.length === 0 && ignoredRequests.length === 0 ? (
                                <div className="empty-state">
                                    <UserPlus size={40} />
                                    <p>No pending requests</p>
                                </div>
                            ) : (
                                <>
                                    {/* Active Connection Requests */}
                                    {activeRequests.map(notif => (
                                        <div key={notif.notificationId} className="notif-item request-card">
                                            <img 
                                                src={notif.senderProfile || "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"} 
                                                alt={notif.senderName}
                                                className="avatar-large" 
                                            />
                                            <div className="content">
                                                <p className="message">
                                                    <span className="bold-name">{notif.senderName}</span> sent you a connection request.
                                                </p>
                                                <span className="time">
                                                    {new Date(notif.createdAt).toLocaleDateString()}
                                                </span>
                                                <div className="action-buttons">
                                                    <button 
                                                        className="btn-accept" 
                                                        onClick={() => handleAccept(notif)}
                                                        title="Accept this connection request"
                                                    >
                                                        <Check size={14} /> Accept
                                                    </button>
                                                    <button 
                                                        className="btn-ignore" 
                                                        onClick={() => handleIgnore(notif)}
                                                        title="Ignore this request"
                                                    >
                                                        <Clock size={14} /> Ignore
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Ignored Requests Section */}
                                    {ignoredRequests.length > 0 && (
                                        <div className="ignored-section">
                                            <div 
                                                className="ignored-header" 
                                                onClick={() => setShowIgnored(!showIgnored)}
                                            >
                                                <div className="ignored-label">
                                                    <Archive size={16} />
                                                    <span>Ignored Requests ({ignoredRequests.length})</span>
                                                </div>
                                                {showIgnored ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </div>

                                            {showIgnored && (
                                                <div className="ignored-list">
                                                    {ignoredRequests.map(notif => (
                                                        <div key={notif.notificationId} className="notif-item request-card ignored-item">
                                                            <img 
                                                                src={notif.senderProfile || "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"} 
                                                                alt={notif.senderName}
                                                                className="avatar" 
                                                            />
                                                            <div className="content">
                                                                <span className="bold-name">{notif.senderName}</span>
                                                            </div>
                                                            <div className="action-buttons small">
                                                                <button 
                                                                    className="btn-accept small" 
                                                                    onClick={() => handleAccept(notif)}
                                                                    title="Accept this connection request"
                                                                >
                                                                    <Check size={12} /> Accept
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* GLOBAL STYLES */}
            <style>{`
                .notif-page { 
                    min-height: 100vh; 
                    background-color: #000; 
                    color: #e1e1e1; 
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; 
                    display: flex; 
                    justify-content: center; 
                }

                .notif-container { 
                    width: 100%; 
                    max-width: 600px; 
                    background-color: #000; 
                    border-left: 1px solid #222; 
                    border-right: 1px solid #222; 
                    min-height: 100vh; 
                    display: flex; 
                    flex-direction: column; 
                }

                /* ✅ Header Styles */
                .page-header { 
                    display: flex; 
                    align-items: center; 
                    padding: 16px; 
                    gap: 16px; 
                    border-bottom: 1px solid #222; 
                    background: #000; 
                    position: sticky; 
                    top: 0; 
                    z-index: 51; 
                }
                
                .page-header h2 { 
                    margin: 0; 
                    font-size: 1.25rem; 
                    font-weight: 700; 
                    color: #fff; 
                }
                
                .back-btn { 
                    background: none; 
                    border: none; 
                    cursor: pointer; 
                    padding: 4px; 
                    border-radius: 50%; 
                    display: flex; 
                    align-items: center; 
                    justify-content: center; 
                    transition: 0.2s; 
                }
                
                .back-btn:hover { 
                    background-color: #181818; 
                }

                /* Tabs */
                .tabs-header { 
                    display: flex; 
                    border-bottom: 1px solid #222; 
                    position: sticky; 
                    top: 64px; /* ✅ Pushed down slightly to account for the new header */
                    background: rgba(0, 0, 0, 0.95); 
                    backdrop-filter: blur(10px); 
                    z-index: 50; 
                }

                .tab-btn { 
                    flex: 1; 
                    background: none; 
                    border: none; 
                    padding: 16px; 
                    color: #71767b; 
                    font-weight: 600; 
                    cursor: pointer; 
                    position: relative; 
                    font-size: 1rem; 
                    transition: all 0.2s ease; 
                    display: flex; 
                    align-items: center; 
                    justify-content: center; 
                    gap: 8px; 
                }

                .tab-btn:hover { 
                    background-color: #181818; 
                    color: #e7e9ea; 
                }

                .tab-btn.active { 
                    color: #fff; 
                }

                .tab-btn.active::after { 
                    content: ''; 
                    position: absolute; 
                    bottom: 0; 
                    left: 25%; 
                    width: 50%; 
                    height: 4px; 
                    background-color: #1d9bf0; 
                    border-radius: 4px 4px 0 0; 
                }

                /* Badge */
                .badge { 
                    background: #333; 
                    color: #fff; 
                    font-size: 0.75rem; 
                    padding: 2px 8px; 
                    border-radius: 12px; 
                    min-width: 20px; 
                    text-align: center; 
                }

                .badge.red { 
                    background: #f4212e; 
                }

                /* Error Banner */
                .error-banner {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 16px;
                    background-color: #ff4d4d22;
                    border-bottom: 1px solid #ff4d4d44;
                    color: #ff8888;
                    font-size: 0.9rem;
                }

                /* Content Area */
                .notif-content { 
                    flex: 1; 
                    overflow-y: auto;
                }

                /* List Group */
                .list-group { 
                    display: flex; 
                    flex-direction: column; 
                }

                /* Notification Item */
                .notif-item { 
                    display: flex; 
                    gap: 14px; 
                    padding: 16px; 
                    border-bottom: 1px solid #1f1f1f; 
                    align-items: flex-start; 
                    transition: background-color 0.2s; 
                }

                .notif-item:hover { 
                    background-color: #080808; 
                }

                .request-card { 
                    align-items: center; 
                }

                /* Avatars */
                .avatar { 
                    width: 40px; 
                    height: 40px; 
                    border-radius: 50%; 
                    object-fit: cover; 
                }

                .avatar-large { 
                    width: 52px; 
                    height: 52px; 
                    border-radius: 50%; 
                    object-fit: cover; 
                    border: 1px solid #333; 
                }

                /* Content */
                .content { 
                    flex: 1; 
                    display: flex; 
                    flex-direction: column; 
                    justify-content: center; 
                }

                .message { 
                    font-size: 0.95rem; 
                    line-height: 1.4; 
                    margin: 0 0 6px 0; 
                    color: #e7e9ea; 
                }

                .bold-name { 
                    font-weight: 700; 
                    color: #fff; 
                }

                .time { 
                    font-size: 0.75rem; 
                    color: #71767b; 
                }

                /* Icon Box */
                .icon-box { 
                    width: 36px; 
                    height: 36px; 
                    border-radius: 50%; 
                    background-color: transparent; 
                    display: flex; 
                    align-items: center; 
                    justify-content: center; 
                    flex-shrink: 0; 
                    margin-top: 2px; 
                }

                /* Action Buttons */
                .action-buttons { 
                    display: flex; 
                    gap: 12px; 
                    margin-top: 8px; 
                }

                .btn-accept { 
                    background-color: #e7e9ea; 
                    color: #000; 
                    border: none; 
                    padding: 6px 20px; 
                    border-radius: 20px; 
                    font-weight: 700; 
                    cursor: pointer; 
                    font-size: 0.9rem; 
                    transition: 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .btn-accept:hover { 
                    background-color: #d7dbdc; 
                }

                .btn-accept:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-ignore { 
                    background-color: transparent; 
                    color: #e7e9ea; 
                    border: 1px solid #536471; 
                    padding: 6px 20px; 
                    border-radius: 20px; 
                    font-weight: 700; 
                    cursor: pointer; 
                    font-size: 0.9rem; 
                    transition: 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .btn-ignore:hover { 
                    background-color: rgba(239, 243, 244, 0.1); 
                }

                /* Delete Button */
                .delete-btn { 
                    background: none; 
                    border: none; 
                    color: #71767b; 
                    cursor: pointer; 
                    padding: 8px; 
                    border-radius: 50%; 
                    transition: 0.2s; 
                }

                .delete-btn:hover { 
                    color: #f4212e; 
                    background-color: rgba(244, 33, 46, 0.1); 
                }

                /* Ignored Section */
                .ignored-section { 
                    margin-top: 10px; 
                    border-top: 1px solid #1f1f1f; 
                }

                .ignored-header { 
                    display: flex; 
                    justify-content: space-between; 
                    align-items: center; 
                    padding: 15px 20px; 
                    cursor: pointer; 
                    color: #71767b; 
                    user-select: none; 
                }

                .ignored-header:hover { 
                    background-color: #080808; 
                    color: #e7e9ea; 
                }

                .ignored-label { 
                    display: flex; 
                    gap: 10px; 
                    align-items: center; 
                    font-weight: 600; 
                    font-size: 0.95rem; 
                }

                .ignored-list { 
                    background-color: #050505; 
                    border-top: 1px solid #1f1f1f; 
                }

                .ignored-item { 
                    padding: 12px 20px; 
                    opacity: 0.7; 
                    transition: opacity 0.2s; 
                }

                .ignored-item:hover { 
                    opacity: 1; 
                }

                .btn-accept.small { 
                    padding: 4px 14px; 
                    font-size: 0.8rem; 
                    height: 30px; 
                }

                .action-buttons.small { 
                    align-items: center; 
                }

                /* Empty State */
                .empty-state { 
                    display: flex; 
                    flex-direction: column; 
                    align-items: center; 
                    justify-content: center; 
                    padding: 80px 20px; 
                    color: #71767b; 
                    gap: 15px; 
                    text-align: center; 
                }

                /* Loading Container */
                .loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 100vh;
                    color: #666;
                    gap: 20px;
                }

                /* Animations */
                @keyframes spin { 
                    100% { transform: rotate(360deg); } 
                }

                /* Mobile Responsive */
                @media (max-width: 600px) {
                    .notif-container { 
                        border: none; 
                        padding-bottom: 80px; 
                    }
                    .avatar-large { 
                        width: 48px; 
                        height: 48px; 
                    }
                    .btn-accept, .btn-ignore { 
                        padding: 6px 16px; 
                        font-size: 0.85rem; 
                    }
                    .tabs-header { 
                        top: 56px; 
                    }
                }
            `}</style>
        </div>
    );
};

// Inline styles for loading state
const styles = {
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        color: '#666',
        gap: '20px',
        backgroundColor: '#000'
    }
};

export default Notification;